<script setup>
    
    import { GoogleMap, MarkerCluster, CustomMarker } from 'vue3-google-map';
    import { useRouteQuery } from '@vueuse/router';
    import { useLocationStore } from '@/stores/location';
    import { useUserStore } from '@/stores/user';
    
    import mapStyles from '@/helpers/mapStyles.json';

    const props = defineProps({
        itinerary: {
            type: Object,
            required: false,
            default: {},
        },
        locations: {
            type: Array,
            required: true,
            default: [],
        },
        mapOnly: {
            type: Boolean,
            required: false,
            default: false,
        },
        heightClass: {
            type: String,
            required: false,
            default: 'full-height',
        },
        content: {
            type: String,
            required: false,
            default: 'site',
        },

        subtractHeight: {
            type: Number,
            required: false,
            default: 0,
        },
        singlePoint: {
            type: Boolean,
            required: false,
            default: false,
        },
        navId: {
            type: String,
            required: false,
            default: 'title-map-list-toggle',
        },
        offset: {
            type: Number,
            required: false,
            default: 32,
        },
        // Set by a listing that is currently filtered, so the map frames its
        // results rather than the downtown default. Defaults OFF like the rest,
        // so the seven surfaces that never filter are untouched.
        frameToLocations: {
            type: Boolean,
            required: false,
            default: false,
        },
        // Both default OFF. This component backs eight surfaces (sites list and
        // detail, events, itineraries, My OHC, My OHC events, stops, and the
        // title-with-map-and-cards block); Asana ...985928/...985927 only asked
        // for the two sites maps, so the other six must render exactly as before.
        zoomControl: {
            type: Boolean,
            required: false,
            default: false,
        },
        showUserLocation: {
            type: Boolean,
            required: false,
            default: false,
        },
        // Opt in to the Asana ...985927 zoom defaults (see applyDefaultFraming).
        // MUST be explicit: the framing used to be gated implicitly, because it
        // bailed out when there were no user coordinates and those required
        // `showUserLocation`. Once the listing branch stopped needing coordinates
        // that gate vanished, and every other map — events, itineraries, My OHC,
        // stops — would have been reframed onto downtown or onto its own point.
        zoomDefault: {
            type: Boolean,
            required: false,
            default: false,
        },
        // Turning zoom on also turns Google's pan and rotate controls OFF (see
        // the template). They render as a SECOND `gm-bundled-control` carrying
        // its own redundant +/- pair, and both containers share the exact same
        // classes — so CSS cannot tell the real zoom control from the duplicate.
        // Switching them off at the API is the only way to keep one set.
        //
        // Google ControlPosition key, e.g. 'RIGHT_BOTTOM'. Undefined leaves the
        // map on its default (top right). The site DETAIL page sets this,
        // because its own map/photo toggle already occupies the top right and
        // the zoom buttons would land underneath it.
        zoomControlPosition: {
            type: String,
            required: false,
            default: undefined,
        },
    });

    const { isOnline } = useOnlineStatus();

    const triggerRebuild = ref(0);
    const showMarkers = ref(true);
    const route = useRoute();

    const isFramed = route.path.includes('/iframe/');

    // Left to Google's 'auto', a phone on a scrollable page gets
    // 'cooperative': one finger scrolls the page and the map wants two, which
    // is wrong for the full-height map views, sized to fill the screen down
    // to the bottom nav — there the map IS the page, and the page can still
    // be scrolled from the header or card carousel. 'greedy' there; the short
    // inline maps (site and stop detail) and the iframe embed, which sit in a
    // page people need to scroll past, stay 'cooperative'.
    const gestureHandling = computed(() => (props.heightClass === 'full-height' && !isFramed ? 'greedy' : 'cooperative'));

    watch(
        () => props.itinerary,
        () => {
            triggerRebuild.value = triggerRebuild.value + 1;
        }
    );

    // Watch for location changes and temporarily hide markers
    watch(
        () => props.locations,
        (newLocations, oldLocations) => {
            if (JSON.stringify(newLocations) !== JSON.stringify(oldLocations)) {
                showMarkers.value = false;
                nextTick(() => {
                    setTimeout(() => {
                        showMarkers.value = true;
                    }, 100);
                });
            }
        },
        { deep: true }
    );

    const current = ref(-1);

    const focus = computed(() => {
        return props.locations?.length > 0 ? props.locations[0]?.fields?.Geocoordinates : '41.8826875,-87.6233125';
    });

    const startingPoint = computed(() => {
        return props.singlePoint && props.locations?.length > 0 ? props.locations[0].value.fields?.Geocoordinates : focus.value;
    });

    const startLatitude = computed(() => {
        return parseFloat(startingPoint.value.split(',')[0].trim());
    });

    const startLongitude = computed(() => {
        return parseFloat(startingPoint.value.split(',')[1].trim());
    });

    const zoom = ref(12);
    const carouselRef = ref(null);

    const center = ref({ lat: startLatitude.value, lng: startLongitude.value });

    const locationStore = useLocationStore();
    const userStore = useUserStore();
    const mapRef = ref(null);

    // Ask for location as soon as a map that supports "here now" mounts.
    //
    // Without this the feature is dormant for almost everyone: GlobalLocation
    // (layouts/default.vue) is the ONLY thing that ever calls
    // locationStore.setCoords, and it renders behind
    // `userStore.hasLocationPermission` — a flag that starts false, is not
    // persisted across loads, and is otherwise only flipped by opting into Near
    // Me or distance sort. So a visitor who just switches to map view was never
    // asked, and both the pin and the default framing never fired.
    //
    // This throws the same switch Near Me already throws (Sites/page.vue,
    // Filters/sites.vue) rather than adding a second geolocation caller.
    //
    // NOTE the flag is misnamed: it means "we want location", not "permission
    // was granted". GlobalLocation queries the real permission state and sets it
    // back to false if the browser reports denied — in which case userCoords
    // stays null and the map keeps the framing it has always had.
    //
    // A WATCHER, not onMounted, and the caller passes the prop reactively:
    // Sites/page.vue renders this component under `v-show="mapView"`, so it is
    // MOUNTED on the plain list view with the map merely hidden. Asking on mount
    // therefore prompted every single visitor to /sites the moment they arrived,
    // map or no map. Verified locally before this fix: div.location was present
    // on /sites with mapSquareVisible false.
    watch(
        () => props.showUserLocation,
        (wanted) => {
            if (wanted) userStore.setHasLocationPermission(true);
        },
        { immediate: true },
    );

    // Deliberately reads `coords`/`hasLocation` and NOT the `location` getter:
    // that getter falls back to the centre of Chicago with accuracy 10000 when
    // permission was never granted, so using it would plant a "you are here"
    // pin in the Loop for someone standing in Evanston.
    const userCoords = computed(() => {
        if (!props.showUserLocation || !locationStore.hasLocation) return null;
        return { lat: locationStore.coords.latitude, lng: locationStore.coords.longitude };
    });

    // ~69 miles per degree of latitude; longitude degrees shrink by cos(lat).
    const MILES_PER_DEGREE_LAT = 69;

    function oneMileBounds(api, { lat, lng }) {
        const dLat = 1 / MILES_PER_DEGREE_LAT;
        const dLng = 1 / (MILES_PER_DEGREE_LAT * Math.cos((lat * Math.PI) / 180));
        return new api.LatLngBounds(
            { lat: lat - dLat, lng: lng - dLng },
            { lat: lat + dLat, lng: lng + dLng },
        );
    }

    // Default framing, per Asana ...985927. The two maps want different things:
    //
    //   - LISTING: one mile around DOWNTOWN CHICAGO, always. It does not depend
    //     on the visitor's location at all, so it frames correctly on first paint
    //     for everyone, permission or not.
    //   - DETAIL: whatever zoom shows the visitor AND the site when the location
    //     is known; one mile around the site when it isn't.
    //
    // The detail map therefore has TWO valid framings, and geolocation usually
    // resolves a beat after the map is ready. So it frames on the site first and
    // UPGRADES once to the visitor-and-site fit if coordinates arrive — tracked
    // by `framedAs` rather than a plain boolean, so the upgrade can happen exactly
    // once and never reverts or loops.
    const DOWNTOWN_CHICAGO = { lat: 41.8826875, lng: -87.6233125 };

    // null | 'downtown' | 'site' | 'site-and-user'
    const framedAs = ref(null);

    function applyDefaultFraming() {
        if (!props.zoomDefault) return;
        // An explicit Locate Me press outranks every automatic framing. Without
        // this the listing map falls through to the `downtown` branch below the
        // moment the next fix arrives and snaps straight back off the visitor.
        if (framedAs.value === 'user') return;
        const instance = mapRef.value;
        if (!instance?.ready || !instance.map || !instance.api) return;

        let bounds;
        let next;

        if (props.singlePoint) {
            const siteLat = startLatitude.value;
            const siteLng = startLongitude.value;
            if (Number.isNaN(siteLat) || Number.isNaN(siteLng)) return;
            const site = { lat: siteLat, lng: siteLng };
            const user = userCoords.value;

            next = user ? 'site-and-user' : 'site';
            // 'site-and-user' is the best framing available; never step back from
            // it, and never redo a framing already applied.
            if (framedAs.value === 'site-and-user' || framedAs.value === next) return;

            if (user) {
                bounds = new instance.api.LatLngBounds();
                bounds.extend(site);
                bounds.extend(user);
            } else {
                bounds = oneMileBounds(instance.api, site);
            }
        } else {
            // The UNFILTERED listing frames one mile around downtown: fitting all
            // 200-odd sites would zoom out to the whole metro. A FILTERED listing
            // frames its own results instead — applying Near Me and being left at
            // the same city-wide zoom, with the pins now clustered into one
            // corner, is what "the cards update but not the map" meant (O2-45).
            //
            // Applying a filter writes to the route query, which remounts this
            // component, so the mount path has to get this right on its own; the
            // watcher below only catches changes within an already-mounted map
            // (typing in the search box).
            if (props.frameToLocations) {
                if (framedAs.value === 'locations') return;
                framedAs.value = 'locations';
                fitToLocations();
                return;
            }
            if (framedAs.value === 'downtown') return;
            next = 'downtown';
            bounds = oneMileBounds(instance.api, DOWNTOWN_CHICAGO);
        }

        framedAs.value = next;
        // Padding only when fitting two points, so neither pin sits on the edge.
        commitFraming(instance, bounds, next === 'site-and-user' ? 48 : 0);
    }

    // `center`/`zoom` are the reactive source of truth for the :center/:zoom
    // bindings. fitBounds moves the map imperatively, so copy the result back
    // once it settles or the next render snaps it straight back.
    function commitFraming(instance, bounds, padding) {
        instance.map.fitBounds(bounds, padding);
        instance.api.event.addListenerOnce(instance.map, 'idle', () => {
            const settled = instance.map.getCenter();
            if (!settled) return;
            center.value = { lat: settled.lat(), lng: settled.lng() };
            zoom.value = instance.map.getZoom();
        });
    }

    // The listing map frames downtown on first paint, but it also has to follow
    // the results. Applying Near Me took the card strip from 202 sites to 39 and
    // the pins from 212 to 18 while the viewport sat frozen at zoom 13 over
    // downtown — which is exactly "the cards update but not the map" (Asana
    // O2-45). Re-fit whenever the plotted set actually changes.
    function fitToLocations() {
        const instance = mapRef.value;
        if (!instance?.ready || !instance.map || !instance.api) return;

        const bounds = new instance.api.LatLngBounds();
        let plotted = 0;
        for (const item of props.locations ?? []) {
            const [lat, lng] = String(item?.fields?.Geocoordinates ?? '')
                .split(',')
                .map((part) => parseFloat(part.trim()));
            if (Number.isNaN(lat) || Number.isNaN(lng)) continue;
            bounds.extend({ lat, lng });
            plotted += 1;
        }
        // Nothing plottable: leave the frame alone rather than fitting an empty
        // box, which Google resolves to the middle of the ocean.
        if (!plotted) return;

        const user = userCoords.value;
        if (user) bounds.extend(user);

        commitFraming(instance, bounds, 48);
    }

    watch(
        // Identity of the plotted set, not a deep compare of several hundred
        // records — the sites themselves never change, only which ones survive
        // the filters.
        () => (props.locations ?? []).map((item) => item?.id).join(','),
        (next, previous) => {
            // Single-point maps have their own two-step framing above, and a map
            // that never opted into default framing shouldn't start moving now.
            if (props.singlePoint || !props.zoomDefault) return;
            if (!props.frameToLocations || next === previous) return;
            // Not before the first framing, or this fights the initial fit.
            if (!framedAs.value) return;
            nextTick(() => fitToLocations());
        },
    );

    // `cameraControl` postdates vue3-google-map 0.15, which exposes no prop for
    // it, so it goes straight onto the map instance. Without this a
    // "Map camera controls" toggle sits mid-right and expands into the very
    // pan/rotate cluster `panControl`/`rotateControl` switch off — carrying a
    // second +/- pair that duplicates ours (Jack spotted the redundancy).
    function applyControlOptions() {
        const instance = mapRef.value;
        if (!props.zoomControl || !instance?.ready || !instance.map) return;
        instance.map.setOptions({ cameraControl: false });
    }

    // Fires on map ready, and again if permission is granted after load.
    watch(
        () => [mapRef.value?.ready, userCoords.value, props.frameToLocations],
        () => nextTick(() => {
            applyControlOptions();
            applyDefaultFraming();
        }),
        { immediate: true },
    );

    // ------------------------------------------------------------------
    // Locate Me (Asana O2-55)
    //
    // The "here now" dot was reported as unreadable — it is the same blue as a
    // cluster bubble — but the deeper problem was that nothing let you ASK to be
    // found. Permission is requested once, silently, on the way into map view;
    // if that fix never lands, or you have since moved, the map offers no way to
    // try again.
    //
    // Two jobs, in order: get a fresh fix, then frame it.
    // ------------------------------------------------------------------
    const locating = ref(false);
    const showLocate = computed(() => props.showUserLocation);

    // Only ever a RESPONSE to pressing the button, never an unprompted notice.
    // GlobalLocation asks for a fix on mount, so a visitor who declined location
    // at some point in the past gets a refusal a second or two after the map
    // loads — and reporting that on its own put a white box over the map on
    // arrival, telling people to change a browser setting they had not asked
    // about. The refusal is only worth mentioning to someone who just asked to
    // be found.
    const locatePressed = ref(false);
    const locateDenied = computed(() => locatePressed.value && locationStore.denied);

    // A press has to survive the round trip: geolocation answers asynchronously,
    // so we record the intent and let the userCoords watcher below do the
    // framing whenever the fix actually lands.
    const awaitingFix = ref(false);
    let locateTimer = null;

    // The refusal is worth saying once, not leaving parked over the map. Long
    // enough to read a short sentence, short enough that it does not become
    // furniture (Jack, 2026-08-28).
    const DENIED_NOTE_MS = 5000;
    let deniedNoteTimer = null;

    function frameOnUser() {
        const instance = mapRef.value;
        const user = userCoords.value;
        if (!instance?.ready || !instance.map || !instance.api || !user) return false;

        let bounds;
        if (props.singlePoint) {
            // The detail map exists to show the site, so keep it in frame and fit
            // both — the same framing this map already applies on load when a fix
            // is available.
            const siteLat = startLatitude.value;
            const siteLng = startLongitude.value;
            if (Number.isNaN(siteLat) || Number.isNaN(siteLng)) return false;
            bounds = new instance.api.LatLngBounds();
            bounds.extend({ lat: siteLat, lng: siteLng });
            bounds.extend(user);
        } else {
            // One mile around you: guarantees the dot is on screen at a zoom that
            // still shows the sites around it, rather than centring at whatever
            // arbitrary zoom the map happened to be left on.
            bounds = oneMileBounds(instance.api, user);
        }

        framedAs.value = 'user';
        commitFraming(instance, bounds, props.singlePoint ? 48 : 0);
        return true;
    }

    function handleLocate() {
        if (locating.value) return;
        locating.value = true;
        awaitingFix.value = true;
        locatePressed.value = true;

        // Drop any standing refusal BEFORE asking again. Pressing this button
        // means "try once more", so the previous answer is no longer current —
        // and the visitor may well have just turned location back on in their
        // browser settings, which is the only way past a denial.
        //
        // It also keeps the denied watcher below honest: it fires on a CHANGE,
        // so leaving the flag set would mean a second refusal never registered
        // and the button span forever.
        locationStore.setDenied(false);

        // Re-prompt. GlobalLocation is rendered behind userStore.hasLocationPermission
        // and UNMOUNTS itself when the browser reports a denial, so flipping the
        // flag back on is what remounts it and re-runs its permission query — the
        // nonce alone would land on a component that is no longer there.
        userStore.setHasLocationPermission(true);
        locationStore.requestFix();

        // Already have a fix: frame it NOW rather than waiting on a watcher that
        // will not fire if the fresh fix is identical to the one we hold. The
        // press is still outstanding though — `awaitingFix` stays true so that a
        // materially better fix landing in the next few seconds reframes once
        // more. (GlobalLocation only republishes after 25m of movement, so a
        // second reframe means the visitor really did move.)
        if (frameOnUser()) locating.value = false;

        // Geolocation can answer neither way — a prompt left sitting, or a denial
        // on a browser that reports nothing back. Release the button regardless
        // so it never sticks in a spinning state.
        if (locateTimer) clearTimeout(locateTimer);
        locateTimer = setTimeout(() => {
            locating.value = false;
            awaitingFix.value = false;
        }, 6000);
    }

    watch(userCoords, (user) => {
        if (!user || !awaitingFix.value) return;
        if (frameOnUser()) {
            awaitingFix.value = false;
            locating.value = false;
            if (locateTimer) clearTimeout(locateTimer);
        }
    });

    // A refusal is terminal until the visitor changes it in browser settings, so
    // stop pretending we are still looking, then retire the message.
    watch(locateDenied, (denied) => {
        if (!denied) return;
        locating.value = false;
        awaitingFix.value = false;
        if (locateTimer) clearTimeout(locateTimer);

        // Dropping `locatePressed` is what hides it: the note and the greyed
        // button both hang off that, so they clear together and the control
        // returns to looking pressable — which it is. Pressing again re-runs the
        // whole sequence, message included.
        if (deniedNoteTimer) clearTimeout(deniedNoteTimer);
        deniedNoteTimer = setTimeout(() => {
            locatePressed.value = false;
        }, DENIED_NOTE_MS);
    });

    onBeforeUnmount(() => {
        if (locateTimer) clearTimeout(locateTimer);
        if (deniedNoteTimer) clearTimeout(deniedNoteTimer);
    });

    // `behavior` is 'smooth' for a user picking a card or a marker, and 'auto'
    // when restoring from the URL on load — coming back should look like the
    // page was never left, not like it animates into place.
    function zoomEvent(item, index, behavior = 'smooth') {
        // #TODO: turn this coordinates fetching into a composable or whatever.
        const coordinates = item?.fields?.Geocoordinates || '';
        const latitude = parseFloat(coordinates.split(',')[0]);
        const longitude = parseFloat(coordinates.split(',')[1]);

        center.value = { lat: latitude, lng: longitude };
        zoom.value = 16;
        current.value = index;

        if (!props.mapOnly) {
            const child = carouselRef.value;
            if (child) {
                const card = child.querySelector(`[data-index="${index}"]`);
                if (card) {
                    card.scrollIntoView({ behavior, inline: 'center', block: 'nearest' });
                }
            }
        }
    }

    const debounce = ref(null);
    
    function handleEvent(val) {
        if (debounce.value) return;
        current.value = val;
        // Put the selection in the URL so it survives leaving the page. See the
        // restore block below for why this is the thing that gets remembered.
        selectedId.value = props.locations?.[val]?.id ?? '';

        debounce.value = setTimeout(() => {
            debounce.value = null;
            const marker = props.locations[val];
            zoomEvent(marker, val);
        }, 250);
    }

    // Calculate distance between title-map-list-toggle bottom and bottom-navigation top
    const outerWrapperRef = ref(null);
    const distanceToBottomNav = ref(0);

    // The retry below used to be unbounded, which was harmless only because this
    // whole function was dead code: the guard was `if (!process.client) return`,
    // and Nuxt 4 compiles `process.client` to a falsy constant, so it returned on
    // every call. Finishing that migration woke it up.
    //
    // Several callers pass no navId and fall back to 'title-map-list-toggle',
    // which exists on Sites/page.vue and Events/page.vue but NOT on site detail
    // or stops pages — verified on staging. There the element never appears, so
    // an unbounded retry is an infinite nextTick -> setTimeout(0) recursion that
    // pins a core for as long as the map is open.
    const MAX_TOGGLE_RETRIES = 20;
    let toggleRetries = 0;
    let pendingRetry = null;

    const calculateDistanceToBottomNav = () => {
        if (!import.meta.client) return

        const titleToggle = document.getElementById(props.navId);

        if (!titleToggle) {
            // Retry only while the toggle itself is still mounting, then give up.
            // Giving up leaves distanceToBottomNav at 0, which is exactly what
            // production has had all along while this was dead.
            if (toggleRetries >= MAX_TOGGLE_RETRIES) return;
            toggleRetries++;
            nextTick(() => {
                pendingRetry = setTimeout(calculateDistanceToBottomNav, 0);
            });
            return;
        }

        toggleRetries = 0;

        // The bottom nav is gone, replaced by the global footer (Asana
        // 1217333501985924), so fall back to the viewport bottom. This used to
        // retry on a missing nav with NO bail-out, which would have spun
        // nextTick -> setTimeout forever on every page with a map.
        const bottomNav = document.getElementById('bottom-navigation');
        const bottomEdge = bottomNav ? bottomNav.getBoundingClientRect().top : window.innerHeight;

        distanceToBottomNav.value = bottomEdge - titleToggle.getBoundingClientRect().bottom - props.offset;
    };

    // --- Restoring the selection across navigation (Asana 1217652839103748) ---
    //
    // Scrolling the list in map view, opening a site and pressing back put you
    // at the top of the list. Only in map view, which is the whole clue: in
    // list view the page itself scrolls and the browser restores it natively,
    // while here the cards live inside .carousel-component, which has its own
    // overflow. Scroll restoration does not reach inside scrolling elements —
    // window.scrollY is 0 before and after, so there was never anything for the
    // browser to put back.
    //
    // Restoring the scroll offset alone was not enough, because the selection
    // is the state that actually matters: `current` is component-local and
    // resets to -1, so the card came back unhighlighted and the map re-framed
    // to its default. Keeping the selected id in the URL fixes all three at
    // once — the history entry the back button returns to already carries it,
    // so the card is selected, the map centres on it, and the list scrolls to
    // it. It survives a hard reload and a shared link for free.
    //
    // Named `selected` rather than `site` because this component also lists
    // events, tours and trails.
    const selectedId = useRouteQuery('selected', '');
    let hasRestoredSelection = false;

    // The carousel lays out asynchronously — the page renders behind `loading`,
    // the map behind v-show, and the container itself is v-if'd on locations.
    // Calling scrollIntoView before it has a height is a SILENT no-op: the card
    // ends up selected and highlighted while the list sits at the top, which is
    // the half-working state this replaced. So wait for the container and the
    // card to actually have a box before scrolling, and give up rather than
    // spin if they never do.
    async function restoreSelectionFromUrl() {
        const id = selectedId.value;
        if (!id || !props.locations?.length) return;

        const index = props.locations.findIndex((item) => item?.id === id);
        // Filters changed under it, or a stale link: leave the list alone
        // rather than guessing at a position.
        if (index < 0) return;

        for (let attempt = 0; attempt < 60; attempt += 1) {
            await nextTick();
            const el = carouselRef.value;
            const card = el?.querySelector(`[data-index="${index}"]`);
            if (el?.clientHeight > 0 && card?.getBoundingClientRect().height > 0) {
                zoomEvent(props.locations[index], index, 'auto');
                return;
            }
            await new Promise((resolve) => requestAnimationFrame(resolve));
        }
    }

    watch(
        () => props.locations?.length,
        (count) => {
            if (hasRestoredSelection || !count || !selectedId.value) return;
            hasRestoredSelection = true;
            restoreSelectionFromUrl();
        },
        { immediate: true, flush: 'post' },
    );

    onMounted(() => {
        // Initial calculation with delay to ensure elements are loaded
        nextTick(() => {
            if (!isFramed.value) setTimeout(calculateDistanceToBottomNav, 0);
            center.value = { lat: startLatitude.value, lng: startLongitude.value }
        });
        if (!isFramed.value) window.addEventListener('resize', calculateDistanceToBottomNav);
    });

    onUnmounted(() => {
        if (!isFramed.value) window.removeEventListener('resize', calculateDistanceToBottomNav);
        // Don't leave a scheduled retry running after the map goes away.
        if (pendingRetry) {
            clearTimeout(pendingRetry);
            pendingRetry = null;
        }
    });

    // CSS variable for distance
    const distanceCssVar = computed(() => {
        return `${distanceToBottomNav.value}px`;
    });
</script>

<template>
    <div class="outer-wrapper" ref="outerWrapperRef" :style="`--distance-to-bottom-nav: ${distanceCssVar}`" :class="{ framed: isFramed }">
        <div class="map-and-carousel-container">
            <ClientOnly>
                <GoogleMap
                    v-if="isOnline"
                    ref="mapRef"
                    api-key="AIzaSyBb3U9fofIbEWHnNnbaAYKM0Au1FzE9yi8"
                    class="map-square"
                    :center="center"
                    :zoom="zoom"
                    :class="[heightClass, zoomControlPosition ? 'zoom-custom-position' : '']"
                    :styles="mapStyles"
                    :disable-default-ui="false"
                    :zoom-control="zoomControl || undefined"
                    :zoom-control-position="zoomControlPosition"
                    :pan-control="zoomControl ? false : undefined"
                    :rotate-control="zoomControl ? false : undefined"
                    :gesture-handling="gestureHandling">
                    <CustomMarker
                        v-if="userCoords"
                        :options="{ position: userCoords, anchorPoint: 'CENTER', zIndex: 1000 }">
                        <div class="user-location-dot" aria-label="Your location" role="img"></div>
                    </CustomMarker>
                    <MarkerCluster v-if="showMarkers && locations && locations.length > 0" :options="{ position: center }">
                        <div v-for="(marker, index) in locations" :key="'marker-' + index">
                            <GlobalMarkerList @click.stop="zoomEvent(marker, index)" :marker="marker" :active="index === current" />
                        </div>
                    </MarkerCluster>
                    <!-- Locate Me. In our own DOM rather than pushed into map.controls:
                         the zoom control is already repositioned by hand in the stylesheet
                         below, and a control handed to Google would be placed by rules this
                         component has spent a lot of CSS overriding.

                         INSIDE GoogleMap, whose root element is .map-square and is
                         position:relative, so these offsets resolve against the MAP. As a
                         sibling of the map they resolved against .map-and-carousel-container,
                         which at desktop takes `height: var(--distance-to-bottom-nav)` — 0 on
                         the site detail page, which put the button at y=-5, off the top of the
                         page behind the nav. -->
                    <div v-if="showLocate" class="locate-control-wrapper" :class="zoomControlPosition ? 'locate-bottom' : 'locate-top'">
                    <button
                        type="button"
                        class="locate-control"
                        :class="{ 'is-locating': locating, 'is-denied': locateDenied }"
                        :aria-busy="locating ? 'true' : 'false'"
                        :aria-label="locateDenied ? 'Location is turned off for this site' : 'Show my location'"
                        :title="locateDenied ? 'Location is turned off for this site' : 'Show my location'"
                        @click.stop="handleLocate">
                        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                            <circle cx="12" cy="12" r="5.5" fill="none" stroke="currentColor" stroke-width="2" />
                            <circle cx="12" cy="12" r="2.25" fill="currentColor" />
                            <path d="M12 1.5v3.5M12 19v3.5M22.5 12H19M5 12H1.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                        </svg>
                    </button>
                        <Transition name="locate-note">
                            <p v-if="locateDenied" class="locate-denied-note" role="status">Turn on location for this site in your browser settings.</p>
                        </Transition>
                    </div>
                </GoogleMap>
                <div v-else class="map-square map-offline-notice" :class="heightClass">
                    <p class="body-book">Map unavailable offline</p>
                </div>
            </ClientOnly>
            <div class="carousel-component"
                v-if="!mapOnly && locations?.length > 0"
                ref="carouselRef"
                :key="triggerRebuild"
                @updateParent="handleEvent">
                <div v-for="(item, index) in locations" class="card-holder" :class="{ 'active-card': item === locations[current] }" :key="item.id" @click="handleEvent(index)" :data-index="index">
                    <CardsSite v-if="content === 'site'" :item="item" type="map" :itinerary="itinerary" />
                    <CardsEvent v-if="content === 'event'" :item="item" type="map" />
                    <CardsTour v-if="content === 'tour'" :item="item" type="map" />
                    <CardsGenericTrail v-if="content === 'generic'" :item="item" type="map" />
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss">

    .outer-wrapper:has(.short) {
        min-height: 0;
    }

    .map-and-carousel-container {

        // The carousel is a fixed height and the map takes whatever is left, so
        // this number is the dial for how much map the user sees. Cut from
        // 208/223 after tightening the card's own padding by 21px
        // (Asana 1217333501985926 / 1217333292818329).
        --heightToSubtract: 187px;
        position: relative;
        display: flex;
        flex-direction: column;
        gap: 10px;

        @media (min-height: 932px) {
            /* Styles specific to iPhone 14 Pro Max viewport */
            --heightToSubtract: 202px;
        }

        @media (min-width: $desktop) {
            flex-direction: row;
            gap: 16px;
        }

        @media (min-width: $desktop) {
            height: var(--distance-to-bottom-nav);

            .outer-wrapper.framed & {
                min-height: calc(60vh - 96px);
                margin-bottom: 32px;

                .map-square.full-height {
                    height: 100%;
                }
            }
        }

        &:has(.short) {
            min-height: 0;
        }

        .carousel-component {
            flex-grow: 1;
            width: 100%;
            background-color: white;
            overflow: hidden;
            overflow-x: auto;
            scroll-snap-type: x mandatory;
            scroll-padding: 13px;
            height: var(--heightToSubtract);
            display: flex;
            gap: 13px;
            flex-direction: row;
            padding: 0 13px 10px 13px;

            .card-holder {
                border: 1px solid $black;
                padding: 1px;
                transition: all 0.25s ease;

                cursor: pointer;

                // The holders stretch to the tallest card in the track, but the
                // card inside used to shrink-wrap its own text — so a short
                // title left dead space below the card and the action row
                // floated up with it. Letting the card fill the holder gives
                // its footer's `margin-top: auto` the slack it needs to sit on
                // the bottom of every card in the carousel.
                display: flex;
                flex-direction: column;

                .card-site {
                    flex: 1;
                }

                &.active-card {
                    border: 2px solid #CC0000;
                    padding: 0px;
                }

                .card-site { 
                    .image-wrapper {
                        display: none;
                    }

                    h3 {
                        display: -webkit-box;
                        -webkit-line-clamp: 2;
                        -webkit-box-orient: vertical;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: normal;

                        @media (min-height: 932px) {
                            -webkit-line-clamp: 3;
                        }
                    }

                    .gray-tag {
                        margin-top: 0;
                    }
                }
            }

            @media (min-width: $desktop) {
                overflow: hidden;
                overflow-y: auto;
                scroll-snap-type: y mandatory;
                scroll-padding: 0;
                background-color: #F5F5F6;
                order: -1;
                max-width: 424px;                
                height: 100%;
                max-height: 100%;
                flex-direction: column; 
                gap: 0;
                padding: 0;

                 .card-holder {
                    // Transparent rather than none, so every card already
                    // reserves the width of the active state's 2px border and
                    // selecting one doesn't nudge the list below it.
                    border: 2px solid transparent;
                    background-color: transparent;
                    border-bottom: 1px solid #D2D3D4; 

                    // The cards stack flush against their dividers in this
                    // column, with nothing between them (Asana O2-13). A little
                    // vertical padding gives each one room to breathe.
                    //
                    // Repeated on the active state on purpose: the base rule sets
                    // `padding: 0` there to make room for its border, and without
                    // this the row would change height when selected.
                    padding: 10px 0;

                    &.active-card {
                        padding: 10px 0;
                    }

                    .card-site {
                        border: none;
                        background-color: transparent;
                        
                        .image-wrapper {
                            display: block;
                        }                        

                        .gray-tag {
                            background-color: white;
                        }
                    }
                 }
            }

            .card-holder {
                scroll-snap-align: start;
                min-width: 271px;
            }

            .card-holder {

                &.active-card {
                    .card-site {
                        background-color: $white;
                        .gray-tag {
                            background-color: #F5F5F6;
                        }                        
                    }   
                }
            }
        }

        .active {
            transform: rotate(90deg);
        }

        .map-square {
            position: relative;
            width: 100%;

            &.full-height {
                height: calc(var(--distance-to-bottom-nav) - var(--heightToSubtract));
                min-height: 280px;

                @media (min-width: $desktop) {
                    height: var(--distance-to-bottom-nav);
                }
            }

            &.short {
                height: 360px;
            }

            .gmnoprint.gm-bundled-control.gm-bundled-control-on-bottom {
                // Street View pegman stays hidden on every map.
                .gm-svpc {
                    display: none;
                }
            }

            // Below tablet the zoom control is hidden, and at tablet and up the
            // block further down drags any bottom-positioned control back to the
            // top right (`gm-bundled-control-on-bottom` is the class Google adds
            // when it IS at the bottom, so that rule cancels the position out).
            // Both are right for maps keeping Google's default placement. The
            // site detail map opts out: its map/photo toggle owns the top right,
            // so the zoom buttons go bottom right instead (Jack, 2026-08-25).
            &:not(.zoom-custom-position) {
                .gmnoprint.gm-bundled-control.gm-bundled-control-on-bottom {
                    .gmnoprint {
                        display: none;
                    }
                }
            }

            .gmnoprint.gm-style-mtc-bbw {
                display: none;
            }

            // Left hidden on purpose. iPhone Safari has no element fullscreen
            // (Google does not draw the control there at all), and the
            // full-height views are already edge to edge, so on phones it
            // would add nothing.
            .gm-fullscreen-control {
                display: none;
            }

            &.map-offline-notice {
                display: flex;
                align-items: center;
                justify-content: center;
                text-align: center;
                background-color: $gray-light;
                border: 1px solid $gray-light-2;
            }

            @media (min-width: $tablet) {
                &:not(.zoom-custom-position) .gmnoprint.gm-bundled-control.gm-bundled-control-on-bottom {
                    top: 0px;
                    right: 0px;
                    bottom: unset;

                    .gmnoprint {
                        top: 0px !important;
                        display: block;
                    }
                }
            }
        }
    }

    // "You are here" dot. PLACEHOLDER pending the Figma reference — deliberately
    // the platform-conventional blue rather than brand turquoise or red, so it
    // reads as the visitor and can't be mistaken for a site marker (red) or a
    // cluster (blue-violet circle with a count).
    .user-location-dot {
        width: 16px;
        height: 16px;
        border-radius: 50%;
        background: #1a73e8;
        border: 2px solid #fff;
        box-shadow: 0 0 0 6px rgba(26, 115, 232, 0.22), 0 1px 3px rgba(0, 0, 0, 0.35);
    }

    // Locate Me control (Asana O2-55).
    //
    // Positioned against the ZOOM control rather than the map corner, because
    // where the zoom control ends up is itself conditional:
    //   - default map: hidden below tablet, top right at tablet and up
    //   - detail map (.zoom-custom-position): bottom right at every width
    // The offsets below are the sizes Google actually renders — a 40px button
    // pair, 81px tall including its divider, inset 10px from the map edge.
    .locate-control-wrapper {
        position: absolute;
        right: 10px;
        z-index: 2;
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 6px;

        &.locate-top {
            // No zoom control at this width, so the button takes the corner, on
            // Google's own 10px inset.
            top: 10px;

            @media (min-width: $tablet) {
                // Clears the 81px zoom pair (pinned to top: 0 by the block
                // above) plus its 10px inset, and leaves the same 10px between
                // the two controls. Flush against it read as one welded block.
                top: 101px;
            }
        }

        &.locate-bottom {
            // Above the zoom pair, matching the reference: locate on top, +/-
            // beneath it. Google insets this map's zoom 24px from the bottom,
            // not 10px — 24 + 81 + 10 of gap.
            bottom: 115px;
        }
    }

    .locate-control {
        width: 40px;
        height: 40px;
        padding: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #fff;
        border: none;
        cursor: pointer;
        color: $black;
        // Measured off this map's own zoom control rather than guessed. Google
        // ships 8px corners on its NEWER control styling — which is what the
        // reference screenshot showed — but the zoom buttons rendered here are
        // the older 2px, so 8px sat next to them looking like a foreign control.
        // Match what actually ships beside it.
        border-radius: 2px;
        box-shadow: rgba(0, 0, 0, 0.3) 0 1px 4px -1px;

        svg {
            width: 22px;
            height: 22px;
            display: block;
        }

        &:hover {
            color: #000;
            background: #f5f5f5;
        }

        &:focus-visible {
            outline: 2px solid $black;
            outline-offset: 2px;
        }

        // Same blue as the dot it is hunting for, so the press reads as connected
        // to the thing that then appears on the map.
        &.is-locating {
            color: #1a73e8;

            svg {
                animation: locate-pulse 1s ease-in-out infinite;
            }
        }

        &.is-denied {
            color: $gray-dark;
        }
    }

    .locate-denied-note {
        max-width: 180px;
        margin: 0;
        padding: 6px 8px;
        background: #fff;
        border-radius: 2px;
        box-shadow: rgba(0, 0, 0, 0.3) 0 1px 4px -1px;
        font-size: 12px;
        line-height: 1.3;
        text-align: right;
    }

    .locate-note-enter-active,
    .locate-note-leave-active {
        transition: opacity 0.25s ease;
    }

    .locate-note-enter-from,
    .locate-note-leave-to {
        opacity: 0;
    }

    @keyframes locate-pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.35; }
    }

    @media (prefers-reduced-motion: reduce) {
        .locate-control.is-locating svg {
            animation: none;
        }

        .locate-note-enter-active,
        .locate-note-leave-active {
            transition: none;
        }
    }
</style>
